<script setup lang="ts">
import { PV } from "@/widgets";
import { ref, watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    placeholder?: string;
    useFakeMask?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    useFakeMask: true,
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);
const fakeMask = ref("secret");

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.Password
                    v-if="model !== undefined"
                    v-model="model"
                    class="rns-input-static"
                    :class="{ 'rns-input-mask': state.updatable || state.removable }"
                    disabled
                    fluid
                    toggleMask
                />
                <PV.Password
                    v-else-if="props.useFakeMask"
                    v-model="fakeMask"
                    class="rns-input-static"
                    :class="{ 'rns-input-mask': state.updatable || state.removable }"
                    disabled
                    fluid
                />
                <PV.Password
                    v-else
                    class="rns-input-static"
                    :class="{ 'rns-input-mask': state.updatable || state.removable }"
                    disabled
                    fluid
                />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.Password
                    v-model="model"
                    :disabled="props.disabled"
                    :feedback="false"
                    fluid
                    :form="props.formId"
                    :inputId="state.id"
                    :invalid="state.hasError"
                    :placeholder="props.placeholder ?? `Masukkan ${label}`"
                    :required="props.required"
                    toggleMask
                    @focusout="actions.onValidate"
                    @keyup.enter.prevent="actions.onSubmit"
                >
                </PV.Password>
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" class="mr-6" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply rns-g-static-input;
}

.rns-input-static :deep(.p-password-input) {
    @apply text-gray-700! bg-transparent! border-none! shadow-none! px-1! pt-2.25!;
}

.rns-input-mask :deep(.p-password-mask-icon),
.rns-input-mask :deep(.p-password-unmask-icon) {
    @apply -mt-1.5! mr-8;
}
</style>
